// /src/pages/ViewNMVTISPage.js
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";
// Keys must match JSON from nmvtis_view (including spaces/case where used).
const COLUMNS = [
  { key: "location", label: "Location" },
  { key: "NMVTIS ID", label: "NMVTIS ID" },
  { key: "PIN", label: "PIN" },
  { key: "REPORTING ENTITY NAME", label: "REPORTING ENTITY NAME" },
  { key: "IS AN INSURANCE ENTITY?", label: "IS AN INSURANCE ENTITY?" },
  { key: "ADDRESS", label: "ADDRESS" },
  { key: "CITY", label: "CITY" },
  { key: "ST", label: "ST" },
  { key: "ZIP", label: "ZIP" },
  { key: "PHONE", label: "PHONE" },
  { key: "EMAIL", label: "EMAIL" },
  { key: "vin", label: "VIN" },
  { key: "Confirm VIN", label: "Confirm VIN" },
  { key: "VEHICLE/VESSEL MAKE", label: "VEHICLE/VESSEL MAKE" },
  { key: "VEHICLE MODEL YEAR", label: "VEHICLE MODEL YEAR" },
  { key: "VEHICLE MODEL NAME", label: "VEHICLE MODEL NAME" },
  { key: "VEHICLE STYLE", label: "VEHICLE STYLE" },
  { key: "MILEAGE", label: "MILEAGE" },
  {
    key: "VEHICLE SALVAGE OBTAIN DATE",
    label: "VEHICLE SALVAGE OBTAIN DATE",
  },
  { key: "VEHICLE DISPOSITION", label: "VEHICLE DISPOSITION" },
  { key: "REASON FOR DISPOSITION", label: "REASON FOR DISPOSITION" },
  {
    key: "VEHICLE INTENDED FOR EXPORT",
    label: "VEHICLE INTENDED FOR EXPORT",
  },
  { key: "RESERVED", label: "RESERVED" },
  {
    key: "INSURANCE OWNER BUSINESS NAME",
    label: "INSURANCE OWNER BUSINESS NAME",
  },
  { key: "INSURANCE OWNER FIRSTNM", label: "INSURANCE OWNER FIRSTNM" },
  { key: "INSURANCE OWNER LASTNM", label: "INSURANCE OWNER LASTNM" },
  { key: "INSURANCE OWNER MI", label: "INSURANCE OWNER MI" },
  { key: "INSURANCE OWNER ADDR", label: "INSURANCE OWNER ADDR" },
  { key: "INSURANCE OWNER CITY", label: "INSURANCE OWNER CITY" },
  { key: "INSURANCE OWNER STATE", label: "INSURANCE OWNER STATE" },
  { key: "INSURANCE OWNER ZIP", label: "INSURANCE OWNER ZIP" },
  {
    key: "VEHICLE TRANSFERRED TO COMPANY",
    label: "VEHICLE TRANSFERRED TO COMPANY",
  },
  {
    key: "VEHICLE TRANSFERRED TO FIRSTNM",
    label: "VEHICLE TRANSFERRED TO FIRSTNM",
  },
  {
    key: "VEHICLE TRANSFERRED TO LASTNM",
    label: "VEHICLE TRANSFERRED TO LASTNM",
  },
  {
    key: "VEHICLE TRANSFERRED TO MI",
    label: "VEHICLE TRANSFERRED TO MI",
  },
  {
    key: "VEHICLE OBTAINED FROM COMPANY",
    label: "VEHICLE OBTAINED FROM COMPANY",
  },
  {
    key: "VEHICLE OBTAINED FROM FIRSTNM",
    label: "VEHICLE OBTAINED FROM FIRSTNM",
  },
  {
    key: "VEHICLE OBTAINED FROM LASTNM",
    label: "VEHICLE OBTAINED FROM LASTNM",
  },
  {
    key: "VEHICLE OBTAINED FROM MI",
    label: "VEHICLE OBTAINED FROM MI",
  },
  { key: "DISMANTLER LOCATION", label: "DISMANTLER LOCATION" },
  { key: "DISMANTLER LIC NUMBER", label: "DISMANTLER LIC NUMBER" },
  { key: "DISMANTLER STOCK NUMBER", label: "DISMANTLER STOCK NUMBER" },
  { key: "TITLING JURISDICTION", label: "TITLING JURISDICTION" },
  { key: "TITLE NO", label: "TITLE NO" },
];

const API_BASE = "/api";

export default function ViewNMVTISPage() {
  const { token } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  // Load data
  useEffect(() => {
    let isCancelled = false;

    async function loadData() {
      setBusy(true);
      setErr("");
      try {
        const res = await fetch(`${API_BASE}/nmvtis`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `HTTP ${res.status}`);
        }

        const data = await res.json();
        if (isCancelled) return;

        const arr = Array.isArray(data) ? data : data.rows || [];
        setRows(arr);
      } catch (e) {
        if (!isCancelled) {
          setErr(e?.message || "Failed to load NMVTIS data.");
        }
      } finally {
        if (!isCancelled) {
          setBusy(false);
        }
      }
    }

    loadData();
    return () => {
      isCancelled = true;
    };
  }, [token]);

  // Unique locations for filter
  const locations = useMemo(() => {
    const set = new Set();
    for (const r of rows) {
      if (r.location) set.add(r.location);
    }
    return Array.from(set).sort();
  }, [rows]);

  // Apply location + search filters
  const filteredRows = useMemo(() => {
    let result = rows;

    if (locationFilter) {
      result = result.filter((r) => r.location === locationFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter((row) =>
        COLUMNS.some((col) => {
          const value = row[col.key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(q);
        })
      );
    }

    return result;
  }, [rows, locationFilter, search]);

  // CSV download (unchanged)
  const handleDownloadCSV = () => {
    if (!filteredRows.length) return;

    const headerLine = COLUMNS.map((c) =>
      `"${c.label.replace(/"/g, '""')}"`
    ).join(",");

    const lines = filteredRows.map((row) =>
      COLUMNS.map((col) => {
        const value = row[col.key];
        if (value === null || value === undefined) return '""';
        const str = String(value);
        return `"${str.replace(/"/g, '""')}"`;
      }).join(",")
    );

    const csv = [headerLine, ...lines].join("\r\n");
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nmvtis_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // TSV download: mark exported_dt for all title_ids, then generate TSV without location.
  // TSV download: mark exported_dt for all title_ids, then generate TSV without location.
const handleDownloadTSV = async () => {
  if (!filteredRows.length) return;

  // Collect title_ids from filtered rows
  const titleIds = filteredRows
    .map((r) => r.title_id)
    .filter((id) => id !== null && id !== undefined);

  if (titleIds.length) {
    try {
      await fetch(`${API_BASE}/nmvtis/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ title_ids: titleIds }),
      });
      // optional: check response / show toast
    } catch (e) {
      console.error("Failed to mark NMVTIS titles exported:", e);
    }
  }

  // Build TSV (exclude "location"; title_id was never in COLUMNS)
  const tsvColumns = COLUMNS.filter((c) => c.key !== "location");

  const headerLine = tsvColumns
    .map((c) =>
      c.label.replace(/\t/g, " ").replace(/\r?\n/g, " ")
    )
    .join("\t");

  const lines = filteredRows.map((row) =>
    tsvColumns
      .map((col) => {
        const value = row[col.key];
        if (value === null || value === undefined) return "";
        return String(value)
          .replace(/\t/g, " ")
          .replace(/\r?\n/g, " ");
      })
      .join("\t")
  );

  const tsv = [headerLine, ...lines].join("\r\n");

  // ⬇️ Tab-separated text file named NVMIS_YYYYMMDD.txt
  const blob = new Blob([tsv], {
    type: "text/plain;charset=utf-8;",
  });

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const filename = `NVMIS_${yyyy}${mm}${dd}.txt`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex flex-wrap gap-3 align-items-center">
              <Link to="/titleagent" className="btn btn-outline-secondary btn-sm">
          ← Back
        </Link>
 
	      <div className="fw-bold">NMVTIS</div>

                {/* Location filter */}
                <Form.Select
                  size="sm"
                  style={{ minWidth: 160 }}
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </Form.Select>

                {/* Search box */}
                <Form.Control
                  size="sm"
                  style={{ minWidth: 220 }}
                  type="text"
                  placeholder="Search all fields..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {/* CSV */}
                <Button
                  size="sm"
                  variant="success"
                  onClick={handleDownloadCSV}
                  disabled={!filteredRows.length}
                >
                  Download Excel (CSV)
                </Button>

                {/* TSV - NMVTIS template */}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleDownloadTSV}
                  disabled={!filteredRows.length}
                  title="This will generate a file ready to upload to NMVTIS"
                >
                  Download NMVTIS Template
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {err && (
                <div className="p-3">
                  <Alert variant="danger" className="mb-0">
                    {err}
                  </Alert>
                </div>
              )}

              {busy ? (
                <div className="p-4 d-flex justify-content-center">
                  <Spinner animation="border" />
                </div>
              ) : filteredRows.length === 0 ? (
                <div className="p-3 text-muted">No records found.</div>
              ) : (
                <div
                  style={{
                    maxHeight: "75vh",
                    overflow: "auto",
                  }}
                >
                  <table className="table table-sm table-striped table-hover mb-0">
                    <thead className="table-light position-sticky top-0">
                      <tr>
                        {COLUMNS.map((col) => (
                          <th key={col.key} className="text-nowrap">
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row, idx) => (
                        <tr key={row.title_id ?? idx}>
                          {COLUMNS.map((col) => {
                            const value = row[col.key];
                            return (
                              <td
                                key={col.key}
                                className="text-nowrap align-middle"
                              >
                                {value === null || value === undefined
                                  ? ""
                                  : String(value)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

